import { useState, useEffect } from "react";

import { useShare } from "@/pages-helpers/teacher/share/useShare";
import { useTeacherNotes } from "@/pages-helpers/teacher/share/useTeacherNotes";
import { TeacherShareNotesButton } from "@/components/TeacherComponents/TeacherShareNotesButton/TeacherShareNotesButton";
import {
  CoreProperties,
  CurriculumTrackingProps,
} from "@/pages-helpers/teacher/share/shareTypes";
import useAnalytics from "@/context/Analytics/useAnalytics";
import { ShareSource } from "@/pages-helpers/teacher/share/createShareId";
import { LessonReleaseCohortValueType } from "@/browser-lib/avo/Avo";
import {
  TeacherNoteCamelCase,
  TeacherNoteError,
  TeacherNote,
} from "@/node-lib/pupil-api/types";

export type UseLessonProps = {
  lessonSlug: string;
  unitSlug?: string;
  programmeSlug?: string;
  source: "lesson-browse" | "lesson-canonical";
  loginRequired: boolean;
  geoRestricted: boolean;
  curriculumTrackingProps: CurriculumTrackingProps & {
    lessonReleaseDate: string;
    lessonReleaseCohort: LessonReleaseCohortValueType;
  };
};

export type TeacherNotesButtonProps = {
  isEditable: boolean | null;
  noteSaved: boolean;
  loginRequired: boolean;
  geoRestricted: boolean;
  onTeacherNotesOpen: () => void;
  shareUrl: string | null;
  shareActivated: (noteLengthChars?: number) => void;
};

type UseLessonReturn = {
  teacherNotesButton: JSX.Element;
  TeacherNotesButtonProps: TeacherNotesButtonProps;
  teacherNoteHtml: string | undefined;
  teacherNotesOpen: boolean;
  setTeacherNotesOpen: (open: boolean) => void;
  shareActivated: (noteLengthChars?: number) => void;
  teacherNote: TeacherNoteCamelCase | null;
  isEditable: boolean | null;
  saveTeacherNote: (
    note: Partial<TeacherNoteCamelCase>,
  ) => Promise<TeacherNote | TeacherNoteError>;
  error: string | null;
  browserUrl: string | null;
  shareUrl: string | null;
};

export const useLesson = ({
  programmeSlug,
  source,
  loginRequired,
  geoRestricted,
  curriculumTrackingProps,
}: UseLessonProps): UseLessonReturn => {
  const [teacherNotesOpen, setTeacherNotesOpen] = useState(false);
  const [lessonPath, setLessonPath] = useState<string | null>(null);

  const appendedSource: ShareSource = `${source}-w-note`;

  const { shareUrl, browserUrl, shareActivated, shareIdRef, shareIdKeyRef } =
    useShare({
      programmeSlug,
      source: appendedSource,
      curriculumTrackingProps,
      overrideExistingShareId: false,
    });

  const { teacherNote, isEditable, saveTeacherNote, noteSaved, error } =
    useTeacherNotes({
      lessonPath,
      shareId: shareIdRef.current,
      sidKey: shareIdKeyRef.current,
      enabled: true,
      curriculumTrackingProps,
    });

  const { track } = useAnalytics();

  const coreTrackingProps: CoreProperties = {
    platform: "owa",
    product: "teacher lesson resources",
    engagementIntent: "advocate",
    componentType: "page view",
    eventVersion: "2.0.0",
    analyticsUseCase: "Teacher",
  };

  useEffect(() => {
    setLessonPath(window.location.href.split("?")[0] || null);
  }, []);

  const handleTeacherNotesOpen = () => {
    setTeacherNotesOpen(true);
    track.teacherNoteDialogueOpened({
      sourcePageSlug: lessonPath,
      ...curriculumTrackingProps,
      ...coreTrackingProps,
      shareId: shareIdRef.current,
      linkUrl: typeof window !== "undefined" ? window.location.href : "",
    });
  };

  const teacherNotesButton = (
    <TeacherShareNotesButton
      isEditable={isEditable}
      noteSaved={noteSaved}
      loginRequired={loginRequired}
      geoRestricted={geoRestricted}
      onTeacherNotesOpen={handleTeacherNotesOpen}
      shareUrl={shareUrl}
      shareActivated={shareActivated}
    />
  );

  const TeacherNotesButtonProps = {
    isEditable,
    noteSaved,
    loginRequired,
    geoRestricted,
    onTeacherNotesOpen: handleTeacherNotesOpen,
    shareUrl,
    shareActivated,
  };

  const teacherNoteHtml = !isEditable ? teacherNote?.noteHtml : undefined;

  return {
    teacherNotesButton,
    TeacherNotesButtonProps,
    teacherNoteHtml,
    teacherNotesOpen,
    setTeacherNotesOpen,
    shareActivated,
    teacherNote,
    isEditable,
    saveTeacherNote,
    error,
    browserUrl,
    shareUrl,
  };
};
